import { useCallback, useState } from 'react';

import type {
  InvestmentBoardPledgedDto,
  InvestmentMoveData,
  MoveInvestmentPipelineCardPayload,
} from '@/api/pipelines/types';

/** The one stage whose move is an approval, and the only one the backend accepts move data on. */
const APPROVAL_STAGE = 'Invested';

export type PendingInvestmentApproval = {
  payload: MoveInvestmentPipelineCardPayload;
  pledged: InvestmentBoardPledgedDto;
};

type PendingState = {
  approval: PendingInvestmentApproval;
  resolve: () => void;
  reject: (_error: unknown) => void;
};

type UseInvestedApprovalOptions = {
  movePipelineCard: (_payload: MoveInvestmentPipelineCardPayload) => Promise<unknown>;
  findPledged: (_cardId: string) => InvestmentBoardPledgedDto | null;
  /** Called instead of moving when the dropped card has no subscription behind it. */
  onRefuse?: (_cardId: string) => void;
};

/**
 * Wraps the board's move call so a drop on `Invested` collects the received
 * amount first. It sits between the mutation and the shared `usePipelineCardMove`,
 * which is untouched — that hook is the deal board's too, and it already calls
 * whatever `movePipelineCard` it is handed.
 *
 * The promise returned for that drop stays pending — neither resolving nor
 * rejecting — for as long as the confirm dialog is open. That is what makes
 * drag-and-drop's own revert-on-catch handling (`usePipelineCardDrag`) put a
 * cancelled or failed card back in its source column. Resolving early instead
 * would leave the card stuck in `Invested`: the invalidation that follows
 * refetches a board still showing the source stage, which is precisely the
 * condition `usePipelineOptimisticBoard` keeps a pending move alive for.
 */
export const useInvestedApproval = ({
  movePipelineCard,
  findPledged,
  onRefuse,
}: UseInvestedApprovalOptions) => {
  const [pending, setPending] = useState<PendingState | null>(null);

  const interceptMove = useCallback(
    (payload: MoveInvestmentPipelineCardPayload): Promise<void> => {
      if (payload.targetStage !== APPROVAL_STAGE) {
        return movePipelineCard(payload).then(() => undefined);
      }

      const pledged = findPledged(payload.cardId);
      if (!pledged) {
        // No pledged figures means no subscription behind the card, so no payload
        // this dialog could produce would be valid. Refuse rather than open it.
        onRefuse?.(payload.cardId);
        return Promise.reject(new Error('No pledged amount to confirm against'));
      }

      return new Promise<void>((resolve, reject) => {
        setPending({ approval: { payload, pledged }, resolve, reject });
      });
    },
    [findPledged, movePipelineCard, onRefuse],
  );

  const confirm = useCallback(
    async (data: InvestmentMoveData) => {
      if (!pending) return;

      try {
        await movePipelineCard({ ...pending.approval.payload, investmentMoveData: data });
        pending.resolve();
      } catch (error) {
        pending.reject(error);
      } finally {
        setPending(null);
      }
    },
    [movePipelineCard, pending],
  );

  const cancel = useCallback(() => {
    pending?.reject(new Error('Cancelled'));
    setPending(null);
  }, [pending]);

  return {
    movePipelineCard: interceptMove,
    pendingApproval: pending?.approval ?? null,
    confirm,
    cancel,
  };
};
